/**
 * SSE parsing for the merged published-app items stream
 * (`GET /v1/published-apps/{app_id}/items/data`).
 *
 * Frame order: `event: tabs` → one `event: item` per widget → one `event: item_summary` per
 * summarised widget → `event: done` with the per-kind counts, the `app_summary` and the per-tab
 * summaries. Items arrive in completion order, not `sort_order` — callers must re-sort for display.
 * The query-less kinds arrive first, since they have nothing to await.
 *
 * **A chart/table `item` frame deliberately carries no `summary` key.** Its summary arrives later in
 * its own `item_summary` frame, so the widget paints without waiting on an LLM call. That makes
 * `summary` a three-state field on those items:
 *
 * - `undefined` — not delivered yet, so the UI shows its "generating" placeholder
 * - `string`    — the summary
 * - `null`      — delivered, but this widget cannot be summarised (errored or no rows)
 *
 * KPI, nugget-feed and text rows still carry `summary: null` on the item frame itself: a KPI is a
 * plain scalar and the query-less kinds have no query result to summarize, so there is nothing to
 * wait for.
 */

import { isAppChartDataItem, type AppChartDataItem } from "@/features/app-studio/api/appChartsApi";
import { isAppKpiDataItem, type AppKpiDataItem } from "@/features/app-studio/api/appKpisApi";
import {
  isAppNuggetFeedDataItem,
  normalizeAppNuggetFeedDataItem,
  type AppNuggetFeedDataItem,
} from "@/features/app-studio/api/appNuggetFeedsApi";
import { asTabFilterField, type TabFilterField } from "@/features/app-studio/api/appTabFiltersApi";
import { isAppTableDataItem, type AppTableDataItem } from "@/features/app-studio/api/appTablesApi";
import {
  isAppTextWidgetDataItem,
  type AppTextWidgetDataItem,
} from "@/features/app-studio/api/appTextWidgetsApi";

export type PublishedAppChartStreamItem = AppChartDataItem & {
  item_type: "chart";
  /** `undefined` until its `item_summary` frame arrives — see the module docstring. */
  summary?: string | null;
};
export type PublishedAppTableStreamItem = AppTableDataItem & {
  item_type: "table";
  /** `undefined` until its `item_summary` frame arrives — see the module docstring. */
  summary?: string | null;
};
export type PublishedAppKpiStreamItem = AppKpiDataItem & {
  item_type: "kpi";
  summary: string | null; // always null — KPIs are not LLM-summarized
};
export type PublishedAppNuggetFeedStreamItem = AppNuggetFeedDataItem & {
  item_type: "nugget_feed";
  summary: string | null; // always null — no query result to summarize
};
export type PublishedAppTextWidgetStreamItem = AppTextWidgetDataItem & {
  item_type: "text";
  summary: string | null; // always null — the widget *is* the text
};
export type PublishedAppStreamItem =
  | PublishedAppChartStreamItem
  | PublishedAppTableStreamItem
  | PublishedAppKpiStreamItem
  | PublishedAppNuggetFeedStreamItem
  | PublishedAppTextWidgetStreamItem;

export type PublishedAppTab = {
  id: number;
  name: string;
  sort_order: number;
};

export type PublishedAppTabSummary = {
  tab_id: number;
  summary: string | null;
};

/** One widget's summary, delivered after its `item` frame. */
export type PublishedAppItemSummary = {
  item_type: "chart" | "table";
  item_id: number;
  summary: string | null;
};

/** The app's own details, sent as the shared stream's first frame. Has no id by design. */
export type AppItemsStreamAppDetail = {
  name: string;
  description: string | null;
};

export type PublishedAppItemsStreamEvent =
  | { type: "item"; item: PublishedAppStreamItem }
  | { type: "itemSummary"; itemSummary: PublishedAppItemSummary }
  | { type: "tabs"; tabs: PublishedAppTab[] }
  // `app` and `filters` are emitted only by the shared-app stream, which needs to hydrate the whole
  // page from one request. The published stream fetches those separately and never sends them.
  | { type: "app"; app: AppItemsStreamAppDetail }
  | { type: "filters"; filters: TabFilterField[] }
  | {
      type: "done";
      chartCount: number;
      tableCount: number;
      kpiCount: number;
      nuggetFeedCount: number;
      textWidgetCount: number;
      appSummary: string | null;
      tabSummaries: PublishedAppTabSummary[];
      /** True when at least one summary tier's stored fingerprint no longer matches its input, so
       * a refresh would produce different text. Drives the builder's `refreshSummaries()` retry. */
      summariesStale: boolean;
    };

function asCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function asNullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

export function parseTabs(value: unknown): PublishedAppTab[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const tabs: PublishedAppTab[] = [];
  for (const raw of value) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const row = raw as Record<string, unknown>;
    if (
      typeof row.id === "number" &&
      typeof row.name === "string" &&
      typeof row.sort_order === "number"
    ) {
      tabs.push({ id: row.id, name: row.name, sort_order: row.sort_order });
    }
  }
  return tabs;
}

export function parseTabSummaries(value: unknown): PublishedAppTabSummary[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const summaries: PublishedAppTabSummary[] = [];
  for (const raw of value) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const row = raw as Record<string, unknown>;
    if (typeof row.tab_id === "number") {
      summaries.push({ tab_id: row.tab_id, summary: asNullableString(row.summary) });
    }
  }
  return summaries;
}

function hasNullableStringSummary(row: Record<string, unknown>): boolean {
  return row.summary === null || typeof row.summary === "string";
}

/** Absent is valid on a chart/table item frame; a present `summary` must still be string-or-null. */
function hasOptionalStringSummary(row: Record<string, unknown>): boolean {
  return row.summary === undefined || hasNullableStringSummary(row);
}

export function isPublishedAppItemSummary(value: unknown): value is PublishedAppItemSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    (row.item_type === "chart" || row.item_type === "table") &&
    typeof row.item_id === "number" &&
    hasNullableStringSummary(row)
  );
}

export function isPublishedAppStreamItem(value: unknown): value is PublishedAppStreamItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  if (row.item_type === "chart") {
    return hasOptionalStringSummary(row) && isAppChartDataItem(row);
  }
  if (row.item_type === "table") {
    return hasOptionalStringSummary(row) && isAppTableDataItem(row);
  }
  if (!hasNullableStringSummary(row)) {
    return false;
  }
  if (row.item_type === "kpi") {
    return isAppKpiDataItem(row);
  }
  if (row.item_type === "nugget_feed") {
    return isAppNuggetFeedDataItem(row);
  }
  if (row.item_type === "text") {
    return isAppTextWidgetDataItem(row);
  }
  return false;
}

/** Normalizes a validated stream item's nested nugget rows (fills optional detail fields). */
export function normalizePublishedAppStreamItem(
  item: PublishedAppStreamItem,
): PublishedAppStreamItem {
  if (item.item_type !== "nugget_feed") {
    return item;
  }
  return { ...item, ...normalizeAppNuggetFeedDataItem(item) };
}

export function parsePublishedAppItemsSseChunk(chunk: string): {
  events: PublishedAppItemsStreamEvent[];
  remainder: string;
} {
  const events: PublishedAppItemsStreamEvent[] = [];
  const normalized = chunk.replace(/\r\n/g, "\n");
  const blocks = normalized.split("\n\n");
  const remainder = blocks.pop() ?? "";

  for (const block of blocks) {
    if (!block.trim()) {
      continue;
    }

    let eventName: string | undefined;
    const dataLines: string[] = [];

    for (const line of block.split("\n")) {
      if (line.startsWith(":")) {
        continue; // heartbeat comment
      }
      if (line.startsWith("event:")) {
        eventName = line.slice("event:".length).trim();
        continue;
      }
      if (line.startsWith("data:")) {
        dataLines.push(line.slice("data:".length).trimStart());
      }
    }

    if (dataLines.length === 0) {
      continue;
    }

    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(dataLines.join("\n")) as Record<string, unknown>;
    } catch {
      continue;
    }

    if (eventName === "done") {
      events.push({
        type: "done",
        chartCount: asCount(payload.chart_count),
        tableCount: asCount(payload.table_count),
        kpiCount: asCount(payload.kpi_count),
        nuggetFeedCount: asCount(payload.nugget_feed_count),
        textWidgetCount: asCount(payload.text_widget_count),
        appSummary: asNullableString(payload.app_summary),
        tabSummaries: parseTabSummaries(payload.tab_summaries),
        summariesStale: payload.summaries_stale === true,
      });
    } else if (eventName === "tabs") {
      events.push({ type: "tabs", tabs: parseTabs(payload.tabs) });
    } else if (eventName === "app" && typeof payload.name === "string") {
      events.push({
        type: "app",
        app: {
          name: payload.name,
          description: typeof payload.description === "string" ? payload.description : null,
        },
      });
    } else if (eventName === "filters") {
      events.push({
        type: "filters",
        filters: Array.isArray(payload.filters) ? payload.filters.map(asTabFilterField) : [],
      });
    } else if (eventName === "item" && isPublishedAppStreamItem(payload)) {
      events.push({ type: "item", item: normalizePublishedAppStreamItem(payload) });
    } else if (eventName === "item_summary" && isPublishedAppItemSummary(payload)) {
      events.push({ type: "itemSummary", itemSummary: payload });
    }
  }

  return { events, remainder };
}
